/**
 * fechaChile.ts — "ahora" y "hoy" SIEMPRE en hora chilena.
 *
 * ── Por qué existe (round 2026-08-05) ────────────────────────────────────
 * `dayjs()` sin argumentos devuelve el instante actual en la zona del
 * DISPOSITIVO. Para un equipo en Chile coincide con lo esperado, pero no es
 * una garantía: un computador con la zona mal configurada, un notebook que
 * viajó, o el navegador de un cliente en el extranjero hacen que "hoy" sea
 * otro día que el del negocio. Y ese "hoy" no es solo decorativo: es el valor
 * por defecto de los DatePicker (la fecha con la que se GUARDA un arriendo, un
 * aseo o una compra) y el mes que se abre en los paneles financieros.
 *
 * El backend y el bot de WhatsApp ya resuelven el día en `America/Santiago`
 * (`backend/src/utils/businessDate.js`, `twilio/src/libs/businessDate.js`).
 * Este módulo cierra el círculo del lado del navegador: TODO el sistema
 * razona en hora chilena, sin importar dónde esté el reloj del usuario.
 *
 * REGLA: en el código de la app se usa `ahoraChile()`, nunca `dayjs()` pelado.
 * `dayjs(valorConcreto)` sí se usa normal — parsear una fecha que ya viene del
 * backend (`"2026-08-31"`, `"12:30"`) no depende de la zona.
 *
 * Ver docs/arquitectura-backend.md §7.1 para la regla equivalente del backend.
 */
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Zona horaria del negocio. Única fuente de verdad del frontend. */
export const TZ_CHILE = "America/Santiago";

/**
 * El instante actual anclado a hora chilena.
 *
 * Reemplazo directo de `dayjs()`: el objeto que devuelve responde
 * `.startOf("month")`, `.format()`, `.add()`, etc. en zona Chile.
 */
export const ahoraChile = (): Dayjs => dayjs().tz(TZ_CHILE);

/** Hoy en hora chilena como `YYYY-MM-DD` (el formato que espera el backend). */
export const hoyChile = (): string => ahoraChile().format("YYYY-MM-DD");

/** El mes actual en hora chilena como `YYYY-MM`. */
export const mesActualChile = (): string => ahoraChile().format("YYYY-MM");
